import {
  acknowledgeErpSyncCursor,
  getErpSyncState,
  isCloudAuthoritativeSession,
  pullErpSyncEvents
} from './erpSessionClient';

export const CLOUD_DATA_CHANGED_EVENT = 'nivora:cloud-data-changed';
export const CLOUD_REALTIME_STATUS_EVENT = 'nivora:cloud-realtime-status';

const DEFAULT_WAIT_MS = 20_000;
const DEFAULT_LIMIT = 100;
const MAX_RETRY_MS = 15_000;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DOMAIN_PATTERN = /^[a-z][a-z0-9_.-]{0,79}$/;

function tenantIdFromActor(actor) {
  const value = String(
    actor?.tenant?.id
      || actor?.tenant_id
      || actor?.tenantId
      || ''
  ).trim().toLowerCase();
  return UUID_PATTERN.test(value) ? value : '';
}

function normalizedCursor(value, fallback = '0') {
  const cursor = String(value == null ? '' : value).trim();
  return /^(?:0|[1-9][0-9]{0,24})$/.test(cursor) ? cursor : fallback;
}

function compareCursor(left, right) {
  const first = normalizedCursor(left);
  const second = normalizedCursor(right);
  if (first.length !== second.length) return first.length < second.length ? -1 : 1;
  if (first === second) return 0;
  return first < second ? -1 : 1;
}

function eventDomain(event) {
  const payloadDomain = String(event?.payload?.domain || '').trim().toLowerCase();
  if (DOMAIN_PATTERN.test(payloadDomain)) return payloadDomain;
  const aggregate = String(event?.aggregate?.type || '').trim().toLowerCase();
  if (aggregate.startsWith('operational.')) {
    const domain = aggregate.slice('operational.'.length);
    return DOMAIN_PATTERN.test(domain) ? domain : '';
  }
  if (aggregate.startsWith('inventory.')) return 'inventory_movements';
  if (aggregate === 'sale_order') return 'orders';
  return DOMAIN_PATTERN.test(aggregate) ? aggregate : '';
}

function eventRecordId(event) {
  const value = String(
    event?.payload?.recordId
      || event?.payload?.record_id
      || event?.aggregate?.id
      || ''
  ).trim().toLowerCase();
  return UUID_PATTERN.test(value) ? value : '';
}

function eventVersion(event) {
  const value = String(
    event?.payload?.version
      || event?.aggregate?.version
      || ''
  ).trim();
  return /^(?:0|[1-9][0-9]{0,18})$/.test(value) ? value : null;
}

function safeDetail(event, tenantId) {
  const cursor = normalizedCursor(event?.cursor, '');
  const domain = eventDomain(event);
  if (!cursor || !domain) return null;
  return Object.freeze({
    tenantId,
    cursor,
    domain,
    recordId: eventRecordId(event) || null,
    version: eventVersion(event),
    type: String(event?.type || 'operational.record.changed.v1').slice(0, 100),
    commandId: UUID_PATTERN.test(String(event?.commandId || '').trim())
      ? String(event.commandId).trim().toLowerCase()
      : null
  });
}

function dispatch(name, detail) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function terminalSessionError(error) {
  return Number(error?.status) === 401
    || ['session_replaced', 'session_not_recoverable'].includes(error?.code);
}

function broadcastRefresh(tenantId, cursor, type) {
  dispatch(CLOUD_DATA_CHANGED_EVENT, Object.freeze({
    tenantId,
    cursor: normalizedCursor(cursor),
    domain: '*',
    recordId: null,
    version: null,
    type: String(type || 'cloud.snapshot.revalidate.v1').slice(0, 100),
    commandId: null
  }));
}

function status(detail, callback) {
  const safe = Object.freeze({
    state: String(detail?.state || 'unknown'),
    cursor: normalizedCursor(detail?.cursor),
    retryInMs: Number(detail?.retryInMs || 0),
    code: detail?.code ? String(detail.code).slice(0, 100) : null
  });
  dispatch(CLOUD_REALTIME_STATUS_EVENT, safe);
  callback?.(safe);
}

function abortableDelay(milliseconds, signal) {
  if (signal?.aborted || milliseconds <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, milliseconds);
    signal?.addEventListener('abort', () => {
      window.clearTimeout(timer);
      resolve();
    }, { once: true });
  });
}

export function subscribeCloudDataChanges(domains, listener) {
  if (typeof window === 'undefined' || typeof listener !== 'function') return () => {};
  const accepted = new Set(
    (Array.isArray(domains) ? domains : [domains])
      .map((domain) => String(domain || '').trim().toLowerCase())
      .filter((domain) => DOMAIN_PATTERN.test(domain))
  );
  const handle = (event) => {
    const domain = event?.detail?.domain;
    if (domain === '*' || !accepted.size || accepted.has(domain)) {
      listener(event?.detail || null);
    }
  };
  window.addEventListener(CLOUD_DATA_CHANGED_EVENT, handle);
  return () => window.removeEventListener(CLOUD_DATA_CHANGED_EVENT, handle);
}

export function subscribeCloudRefresh(domains, listener, options = {}) {
  if (typeof window === 'undefined' || typeof listener !== 'function') return () => {};
  const delayMs = Math.min(2_000, Math.max(0, Number(options.delayMs ?? 250)));
  let timer = null;
  let pendingDetail = null;
  let stopped = false;

  const flush = () => {
    timer = null;
    if (stopped || !pendingDetail) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const detail = pendingDetail;
    pendingDetail = null;
    listener(detail);
  };

  const schedule = (detail) => {
    pendingDetail = detail || pendingDetail || Object.freeze({});
    if (timer !== null) return;
    timer = window.setTimeout(flush, delayMs);
  };

  const handleVisibility = () => {
    if (
      typeof document !== 'undefined'
      && document.visibilityState === 'visible'
      && pendingDetail
    ) {
      schedule(pendingDetail);
    }
  };

  const unsubscribe = subscribeCloudDataChanges(domains, schedule);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibility);
  }

  return () => {
    stopped = true;
    unsubscribe();
    if (timer !== null) window.clearTimeout(timer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibility);
    }
  };
}

export function startCloudRealtime(actor, options = {}) {
  const tenantId = tenantIdFromActor(actor);
  if (!tenantId || !isCloudAuthoritativeSession(actor)) return () => {};
  if (typeof window === 'undefined') return () => {};

  const controller = new AbortController();
  const pull = options.pullEvents || pullErpSyncEvents;
  const acknowledge = options.acknowledgeCursor || acknowledgeErpSyncCursor;
  const readState = options.getSyncState || getErpSyncState;
  const waitMs = Math.min(
    DEFAULT_WAIT_MS,
    Math.max(0, Number(options.waitMs ?? DEFAULT_WAIT_MS))
  );
  const limit = Math.min(
    DEFAULT_LIMIT,
    Math.max(1, Number(options.limit ?? DEFAULT_LIMIT))
  );

  let cursor = '0';
  void (async () => {
    let retryMs = 1000;
    let initialized = false;
    while (!controller.signal.aborted && !initialized) {
      try {
        const initial = await readState();
        if (controller.signal.aborted) return;
        // Uma falha no bootstrap nunca pode cair para cursor zero: isso faria
        // um navegador novo percorrer todo o historico do tenant. A pagina ja
        // le o snapshot atual pela API e o stream parte do cursor corrente.
        cursor = normalizedCursor(
          initial?.serverCursor
            || initial?.deliveredCursor
            || initial?.acknowledgedCursor,
          '0'
        );
        initialized = true;
        retryMs = 1000;
        status({ state: 'connected', cursor }, options.onStatus);
        // Fecha a janela entre o primeiro fetch da rota e a captura do cursor.
        // Cada tela aberta recebe apenas um refetch do seu proprio dominio.
        broadcastRefresh(tenantId, cursor, 'cloud.snapshot.revalidate.v1');
      } catch (error) {
        if (controller.signal.aborted) return;
        if (terminalSessionError(error)) {
          status({
            state: 'stopped',
            cursor,
            code: error?.code || 'unauthorized'
          }, options.onStatus);
          return;
        }
        status({
          state: 'retrying',
          cursor,
          retryInMs: retryMs,
          code: error?.code || 'initial_state_failed'
        }, options.onStatus);
        await abortableDelay(retryMs, controller.signal);
        retryMs = Math.min(MAX_RETRY_MS, retryMs * 2);
      }
    }

    let mustRevalidateAfterReconnect = false;
    while (!controller.signal.aborted) {
      try {
        const page = await pull({
          after: cursor,
          limit,
          waitMs,
          signal: controller.signal
        });
        if (controller.signal.aborted) return;
        const events = Array.isArray(page?.events) ? page.events : [];
        let nextCursor = cursor;
        for (const event of events) {
          const eventCursor = normalizedCursor(event?.cursor, '');
          if (!eventCursor || compareCursor(eventCursor, nextCursor) <= 0) continue;
          nextCursor = eventCursor;
          const detail = safeDetail(event, tenantId);
          if (detail) dispatch(CLOUD_DATA_CHANGED_EVENT, detail);
        }
        const pageCursor = normalizedCursor(page?.cursor, nextCursor);
        if (compareCursor(pageCursor, nextCursor) > 0) nextCursor = pageCursor;
        if (compareCursor(nextCursor, cursor) > 0) {
          cursor = nextCursor;
          await acknowledge(cursor);
        }
        if (mustRevalidateAfterReconnect) {
          mustRevalidateAfterReconnect = false;
          broadcastRefresh(tenantId, cursor, 'cloud.reconnected.revalidate.v1');
        }
        retryMs = 1000;
        status({ state: 'connected', cursor }, options.onStatus);
      } catch (error) {
        if (controller.signal.aborted || error?.name === 'AbortError') return;
        if (terminalSessionError(error)) {
          status({
            state: 'stopped',
            cursor,
            code: error?.code || 'unauthorized'
          }, options.onStatus);
          return;
        }
        mustRevalidateAfterReconnect = true;
        status({
          state: 'retrying',
          cursor,
          retryInMs: retryMs,
          code: error?.code || 'network_unavailable'
        }, options.onStatus);
        await abortableDelay(retryMs, controller.signal);
        retryMs = Math.min(MAX_RETRY_MS, retryMs * 2);
      }
    }
  })();

  return () => {
    controller.abort();
    status({ state: 'stopped', cursor }, options.onStatus);
  };
}

export const __cloudRealtimeTestUtils = Object.freeze({
  compareCursor,
  eventDomain,
  safeDetail,
  tenantIdFromActor
});
